import http from './http';
import { streamMessages } from './messagesStreaming';

// Legacy individual message record
export interface ApiMessage {
  id: number;
  content: string;
  sheet_id: number;
  chat_id: number;
  is_ai_message: boolean;
  created_at?: string;
}

// New combined record: backend returns a pair (user + ai) as a single row
export interface CombinedMessage {
  id: number; // pair id
  chat_id: number;
  sheet_id: number;
  user_request: string;
  ai_reply: string;
  ai_reply_structured?: { blocks: unknown[] } | null;
  created_at?: string;
}

export type StreamingChatPayload = {
  id?: number | string;
  title?: string;
  account_id?: number | string;
};

export type StreamingEventPayload = {
  status?: string;
  message?: string;
  response?: unknown;
  chat?: StreamingChatPayload;
};

// Legacy create response (two separate messages)
export type LegacyCreatePair = {
  user_message: ApiMessage;
  ai_message: ApiMessage;
};

export type CreateMessageResponse = CombinedMessage | CombinedMessage[] | LegacyCreatePair;

export type StreamingSession = {
  iterator: AsyncGenerator<StreamingEventPayload, void, void>;
  cancel: () => void;
  done: Promise<{ aborted: boolean }>;
};

export const messagesApi = {
  async list(
    accountId: string | number,
    chatId: string | number,
    token?: string
  ): Promise<CombinedMessage[]> {
    const res = await http.get(
      `/v1/accounts/${accountId}/chats/${chatId}/messages`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
    return res.data as CombinedMessage[];
  },

  async create(
    accountId: string | number,
    chatId: string | number,
    data: { content: string; sheet_id: number; is_ai_message: boolean },
    token?: string
  ): Promise<CreateMessageResponse> {
    const res = await http.post(
      `/v1/accounts/${accountId}/chats/${chatId}/messages`,
      data,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
    return res.data as CreateMessageResponse;
  },

  async remove(
    accountId: string | number,
    chatId: string | number,
    messageId: string | number,
    token?: string
  ): Promise<unknown> {
    const res = await http.delete(
      `/v1/accounts/${accountId}/chats/${chatId}/messages/${messageId}`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
    return res.data as unknown;
  },

  async createStreaming(
    accountId: string | number,
    chatId: string | number | null,
    data: {
      content: string;
      sheet_id?: number | null;
      category_ids?: number[];
      selected_tag_ids?: number[];
      chat_id?: string | number | null;
      workflow?: string;
      files?: File[];
    },
    token?: string,
    onEvent?: (evt: StreamingEventPayload) => void
  ): Promise<StreamingSession> {
    const controller = new AbortController();
    const hasCategoryIds = !!data.category_ids?.length;
    const iterator = streamMessages({
      accountId,
      chatId,
      routeNamespace: hasCategoryIds ? 'pif-report' : 'pif-report-sheet',
      sheetId: hasCategoryIds ? undefined : data.sheet_id,
        payload: {
          content: data.content,
          category_ids: data.category_ids,
          selected_tag_ids: data.selected_tag_ids,
          chat_id: data.chat_id,
          workflow: data.workflow,
        },
      files: data.files,
      token,
      signal: controller.signal,
    });
    const done = (async () => {
      try {
        for await (const event of iterator) {
          onEvent?.(event);
        }
        return { aborted: false } as const;
      } catch (error) {
        const name = (error as DOMException | Error)?.name;
        if (name === 'AbortError') {
          return { aborted: true } as const;
        }
        throw error;
      }
    })();

    const cancel = () => {
      controller.abort();
      if (typeof iterator.return === 'function') {
        void iterator.return(undefined).catch(() => {});
      }
    };

    return {
      iterator,
      cancel,
      done,
    };
  },
};

export default messagesApi;
